import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import './emails.css';
import getEmailTemplatesList from '../../../api/graphql/queries/getEmailTemplatesList';
import getEmailTemplatePreview from '../../../api/graphql/queries/getEmailTemplatePreview';
import getEmailScopePlates from '../../../api/graphql/queries/getEmailScopePlates';
import { renderBlocks } from '../../../utils/emailBlocks';
import { docFromTemplate } from '../../../utils/emailBlockDoc';
import { substituteSampleData } from '../../../utils/emailSampleRender';
import {
  buildPlateStrip, PLATE_STRIP_LOADING, PLATE_STRIP_READY, PLATE_STRIP_FAILED,
} from '../../../utils/emailPlates';
import formatTimestamp from '../../../utils/formatTimestamp';
import languages from '../../../utils/languages';
import Modal from '../../Modal/Modal';
import EmailProofPane from './EmailProofPane';
import EmailCloneModal from './EmailCloneModal';
import LoadIcon from '../../LoadIcon';

// Detail view for a single email template, reached by clicking a 'present'
// plate on the Emails list (/cms/emails/:scope/:language). Two independent
// loads happen here: the template metadata (to know it exists at all, and to
// get its templateId), then the server-rendered preview (which needs that
// templateId). They are tracked with separate loading flags because a slow
// preview render should not hold up showing the metadata that's already in.
//
// formatTimestamp (src/utils/formatTimestamp.js) handles the epoch-millisecond
// string Mongoose timestamps arrive as - see that file for the trap it avoids.
// Same treatment the Logs tab gives its createdAt column (LogsContainer.jsx),
// and shared with EmailRevisionHistory.jsx's revisions[].savedAt.
//
// A read-only look at one plate: which plate, whether it sends, what it is
// called, which variables it uses, the email itself, and the way through to
// editing or cloning.
//
// The email is rendered from the BLOCKS, not from the stored htmlContent, for
// the reason set out at `proofHtml` below. A rendered-subject readout and a
// stale-HTML banner used to sit here too and are gone: the first duplicated the
// subject shown a few pixels to its left, and the second reported a mismatch
// nobody could act on from this screen.

const EmailTemplateDetail = ({ match }) => {
  const { scope, language } = match.params;

  const [template, setTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [preview, setPreview] = useState(null);

  // The campaign's other plates. This screen shows only its own plate, so it did
  // not need them — the clone modal does. It lists exactly what a clone will copy,
  // and passing nothing would let it render a confident empty list instead.
  const [scopePlates, setScopePlates] = useState([]);
  const [platesStatus, setPlatesStatus] = useState(PLATE_STRIP_LOADING);
  const [isCloneModalOn, setIsCloneModalOn] = useState(false);

  /*
  The rendered email, held in state rather than derived during render.

  It IS pure — blocks in, HTML out — so a `const` in the render body computed the
  right string, and the iframe still came up blank with 11k characters sitting in
  its srcdoc attribute. Chrome does not paint a srcDoc supplied on an iframe's
  very first render; it paints one that arrives as an update. The editor's proof
  never hit this because its own HTML starts empty and lands via a debounced
  effect, which is an update by accident.

  So this does the same thing on purpose: empty at mount, filled in an effect.
  */
  const [proofHtml, setProofHtml] = useState('');

  useEffect(() => {
    // The plate strip links between languages on this same route, so React
    // Router reuses this mounted component instance and only swaps
    // match.params — it does not remount. Without this guard, a slow response
    // for a previous scope/language can resolve after a newer one and render
    // the wrong template under the current URL. `ignore` is scoped to this
    // specific effect invocation (via the closure), so only *this* run's
    // response is silenced by the cleanup below.
    let ignore = false;

    setIsLoading(true);
    setNotFound(false);
    setTemplate(null);
    setPreview(null);

    console.log('Calling getEmailTemplatesList()');
    getEmailTemplatesList({ scope, language }).then((templatesDataObject) => {
      if (ignore) {
        return;
      }
      if (templatesDataObject) {
        if (templatesDataObject.isAuth) {
          const [found] = templatesDataObject.templates || [];
          if (found) {
            setTemplate(found);
          } else {
            // A missing template for this scope/language is a normal, expected
            // outcome — reachable both from a 'missing' plate's scope/language
            // combination via a direct or bookmarked URL, and from any
            // scope/language pair with no plate at all — not an error.
            setNotFound(true);
          }
          setIsLoading(false);
        } else {
          // Matches the isAuth convention used across the Emails tab: log and
          // leave isLoading true rather than falling through to a render that
          // assumes `template` is set.
          console.log('getEmailTemplatesList failed!');
        }
      } else {
        console.log('templatesData = null');
      }
    });

    return () => {
      ignore = true;
    };
  }, [scope, language]);

  useEffect(() => {
    // Every plate of this campaign, for the clone modal's "what will be copied"
    // list. Keyed on `scope` alone: switching language inside the same campaign
    // does not change which plates exist. Same staleness guard as every other
    // fetch on this screen.
    let ignore = false;

    setScopePlates([]);
    setPlatesStatus(PLATE_STRIP_LOADING);

    console.log('Calling getEmailScopePlates()');
    getEmailScopePlates({ scope }).then((platesDataObject) => {
      if (ignore) {
        return;
      }
      if (platesDataObject && platesDataObject.isAuth) {
        setScopePlates(platesDataObject.templates || []);
        setPlatesStatus(PLATE_STRIP_READY);
      } else {
        // FAILED, not an empty list. An empty list is a positive claim that this
        // campaign has no other plates, and the clone modal says so out loud —
        // which would be a lie told right before someone copies a campaign.
        setPlatesStatus(PLATE_STRIP_FAILED);
      }
    });

    return () => {
      ignore = true;
    };
  }, [scope]);

  useEffect(() => {
    if (!template) {
      // Reachable right after the effect above resets `template` to null on a
      // scope/language change — must not fire a preview fetch with an
      // undefined templateId.
      return undefined;
    }

    // Same staleness guard as above: a plate click while a preview fetch for
    // the previous template is still in flight must not let that late
    // response overwrite the newly-selected template's preview state.
    let ignore = false;

    console.log('Calling getEmailTemplatePreview()');
    getEmailTemplatePreview({ templateId: template.templateId }).then((previewDataObject) => {
      if (ignore) {
        return;
      }
      if (previewDataObject) {
        if (previewDataObject.isAuth) {
          if (previewDataObject.preview) {
            setPreview(previewDataObject.preview);
          }
          // A preview that does not render is no longer a visible failure: this
          // screen stopped showing the proof, and the only thing still read off
          // `preview` is which variables the sender does not provide. Absent that
          // answer the chips simply make no claim, which is the right silence.
        } else {
          console.log('getEmailTemplatePreview failed!');
        }
      } else {
        console.log('previewData = null');
      }
    });

    return () => {
      ignore = true;
    };
  }, [template]);

  useEffect(() => {
    /*
    For a block-authored plate the blocks ARE the document: the stored htmlContent
    is regenerated from them on every save, so they are both what the editor shows
    and what the next save will send. Rendering them here means this screen and the
    editor cannot disagree about the same email — which they did, for as long as
    this pane drew the stored HTML instead.

    A plate with no blocks has nothing else to draw from and falls back to the
    server's render of the stored HTML, which is also what it sends.
    */
    if (!template) {
      setProofHtml('');
      return;
    }
    const blockDoc = docFromTemplate(template);
    if (blockDoc) {
      const sampleJson = JSON.stringify(template.sampleData || {});
      setProofHtml(substituteSampleData(renderBlocks(blockDoc), sampleJson));
    } else {
      setProofHtml((preview && preview.html) || '');
    }
  }, [template, preview]);

  if (isLoading) {
    return (
      <div className="emails-app">
        <div className="emails-loading">
          <LoadIcon />
          Loading template…
        </div>
      </div>
    );
  }

  // A missing plate is a normal outcome, as the load handler above already says.
  // So it offers the thing you came here to do rather than only a way back: the
  // editor's own empty state writes the plate.
  if (notFound) {
    return (
      <div className="emails-app">
        <div className="emails-empty">
          No
          {' '}
          <strong>{language.toUpperCase()}</strong>
          {' email exists for '}
          <strong>{scope}</strong>
          {' yet.'}
          <br />
          <Link to={`/cms/emails/${scope}/${language}/edit`} className="btn">Write this plate</Link>
          <br />
          <Link to="/cms/emails">← Back to Emails</Link>
        </div>
      </div>
    );
  }

  // Built the same way the editor builds its strip, so the clone modal is told the
  // same three truths: ready, still loading, or the fetch failed. `currentTemplate`
  // is this plate, which is on screen and therefore exists whatever the strip fetch
  // did — without it a failed fetch would list this very plate as absent.
  const strip = buildPlateStrip({
    status: platesStatus,
    templates: scopePlates,
    languages,
    currentLanguage: language,
    currentTemplate: template,
  });

  return (
    <div className="emails-app">
      <div className="toolbar centered">
        <div className="titleblock">
          <Link to="/cms/emails" className="btn quiet">
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M8.5 2L3.5 7l5 5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            All campaigns
          </Link>
          <div>
            {/*
              The campaign is the headline, not `name`.

              `name` is a machine id that duplicates templateId — every one in
              this corpus is exactly `<scope>-<language>` — and it was the biggest
              text on the screen. It also drifts: a clone copied it verbatim, so a
              campaign cloned to test_jose_2027 announced itself as
              ps-presalesbcn26-en. That copy is fixed, but the deeper problem was
              giving a redundant, drift-prone field the most prominent position.

              The scope is what identifies this plate and cannot go stale — it is
              the campaign, the Braze flag and the routing key at once. templateId
              stays underneath, quiet, where a technical id belongs.
            */}
            <div className="tname">{template.scope}</div>
            <div className="scope-key tkey">{template.templateId}</div>
          </div>
        </div>

        {/*
          The whole campaign's plates, not just this one. This drew a single cell
          for the language you happened to be on, so there was no way to move
          between a campaign's languages from here at all — the strip on the list
          screen was the only route, and it means going back out first. The data
          was already being fetched for the clone modal.
        */}
        <div className="field">
          <span className="field-label">Plate</span>
          <span className="plates plateswitch">
            {strip.plates.map((plate) => {
              // Not known yet, or the fetch failed: a placeholder that makes no
              // claim either way. Never an empty square — that asserts the plate
              // does not exist.
              if (!plate.known) {
                return (
                  <span
                    key={plate.language}
                    className={strip.status === PLATE_STRIP_FAILED ? 'plate-cell is-unknown is-failed' : 'plate-cell is-unknown'}
                    title={strip.status === PLATE_STRIP_FAILED
                      ? 'This campaign’s other plates could not be loaded — this is not a statement that they are missing'
                      : 'Still checking which plates this campaign has'}
                  >
                    ·
                  </span>
                );
              }

              if (plate.isCurrent) {
                return (
                  <span
                    key={plate.language}
                    className={`plate-cell ${plate.state} is-current`}
                    title={`${plate.language.toUpperCase()} — the plate you are looking at`}
                  >
                    {plate.language}
                  </span>
                );
              }

              // A missing plate still links: its destination offers to write it.
              return (
                <Link
                  key={plate.language}
                  to={`/cms/emails/${scope}/${plate.language}`}
                  className={`plate-cell ${plate.state} is-dimmed`}
                  title={plate.exists
                    ? `${plate.language.toUpperCase()} — open this plate`
                    : `No ${plate.language.toUpperCase()} plate yet — open to write it`}
                >
                  {plate.language}
                </Link>
              );
            })}
          </span>
        </div>

        <div className="field">
          <span className="field-label">Active</span>
          <span className="swline">
            <span
              className={template.isActive ? 'sw on' : 'sw'}
              role="img"
              aria-label={template.isActive ? 'Active' : 'Inactive'}
            />
            <span className="swtext">{template.isActive ? 'sending' : 'not sending'}</span>
          </span>
        </div>

        <div className="toolbar-actions">
          <span className="locked">{`Saved ${formatTimestamp(template.updatedAt)}`}</span>
          {/*
            Clone lives here, not only inside the editor. Cloning copies the whole
            campaign — it is a thing you do TO this campaign, not an editing action
            on one plate — so making it reachable only from the plate editor put a
            "Save plate" screen between you and it for no reason.
          */}
          <button type="button" className="btn quiet" onClick={() => setIsCloneModalOn(true)}>
            Clone
          </button>
          <Link to={`/cms/emails/${scope}/${language}/edit`} className="btn">Edit plate</Link>
        </div>
      </div>

      {template.needsReview && (
        <div className="banner">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#8a5a12" strokeWidth="1.7">
            <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
            <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
          </svg>
          <span>
            <b>This plate is flagged for review.</b>
            {' It was copied word for word from another campaign — check the dates, links and copy before it sends.'}
          </span>
        </div>
      )}

      {preview && preview.unprovidedVariables && preview.unprovidedVariables.length > 0 && (
        <div className="banner">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#8a5a12" strokeWidth="1.7">
            <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
            <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
          </svg>
          <span>
            <b>Unprovided variables.</b>
            {' These render as empty strings at send time because nothing supplies them: '}
            {preview.unprovidedVariables.join(', ')}
          </span>
        </div>
      )}

      <div className="press">
        <div className="stack">
          <div className="panel">
            <div className="head">
              <h4>Inbox line</h4>
              <div className="right">
                <span className="locked">{template.language.toUpperCase()}</span>
              </div>
            </div>
            <div className="body">
              <div className="field">
                <span className="field-label">Subject</span>
                <span>{template.subject}</span>
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="head"><h4>Variables</h4></div>
            <div className="body">
              <div className="chips">
                {template.variables && template.variables.length > 0 ? (
                  template.variables.map(variableName => (
                    <span
                      key={variableName}
                      className={preview && preview.unprovidedVariables
                        && preview.unprovidedVariables.includes(variableName)
                        ? 'chip broken'
                        : 'chip used'}
                    >
                      {variableName}
                    </span>
                  ))
                ) : (
                  <span className="chip unused">This plate uses no variables</span>
                )}
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="head">
              <h4>Template</h4>
              <div className="right">
                <span className="locked">{template.templateId}</span>
              </div>
            </div>
            <div className="body">
              <p className="hint">
                <b>Source</b>
                {` · ${template.source || 'unknown'}`}
              </p>
              <p className="hint">
                <b>Last saved</b>
                {` · ${formatTimestamp(template.updatedAt)}`}
              </p>
            </div>
          </div>
        </div>

        <EmailProofPane
          title="Email"
          fromName={template.appName || 'Sender'}
          subject={preview ? preview.subject : template.subject}
          preheader={template.preheader}
          html={proofHtml}
          text={preview ? preview.text : template.textContent}
        />
      </div>

      {isCloneModalOn && (
        <Modal
          title="Clone campaign"
          handleClick={() => setIsCloneModalOn(false)}
          type="clone"
        >
          <EmailCloneModal
            sourceScope={scope}
            sourcePlates={strip.plates}
            sourcePlatesStatus={strip.status}
            onClose={() => setIsCloneModalOn(false)}
          />
        </Modal>
      )}
    </div>
  );
};

EmailTemplateDetail.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      scope: PropTypes.string.isRequired,
      language: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default EmailTemplateDetail;
